import TelemetryProvider from 'common/utilities/TelemetryProvider';

import { NotificationSettingType } from './NotificationSettingsPanel/NotificationSettings.types';
import { OfficeShellPanelState, OfficeShellPanelType } from './OfficeShellPanel.types';

// Since side panel can be opened on any page (with office shell), we'll prefix the params used for this deep link to avoid potential page param conflicts
const paramPrefix = 'sidePanel_';

function getSidePanelParam(urlParams: URLSearchParams, paramName: string) {
  return urlParams.get(`${paramPrefix}${paramName}`);
}

const sidePanelDeepLinkParamParsers = {
  NotificationSettings(params: URLSearchParams) {
    const settingTypeLookup: { [NST in NotificationSettingType]: NST } = {
      Push: 'Push',
      Email: 'Email',
      Family: 'Family',
    };

    return {
      panelType: 'NotificationSettings' as const,
      deepLinkParams: {
        settingType: settingTypeLookup[getSidePanelParam(params, 'settingType') as NotificationSettingType] || undefined,
        familyMemberCsid: getSidePanelParam(params, 'familyMemberCsid') || undefined,
      },
    };
  },
  FamilySettings(params: URLSearchParams) {
    return {
      panelType: 'FamilySettings' as const,
      deepLinkParams: {},
    };
  },
  PrivacySettings(params: URLSearchParams) {
    return {
      panelType: 'PrivacySettings' as const,
      deepLinkParams: {},
    };
  },
  GlobalCopilot(params: URLSearchParams) {
    return {
      panelType: 'GlobalCopilot' as const,
      deepLinkParams: {},
    };
  },
}; // TODO: use `satisfies { [PT in OfficeShellPanelType]: (params: URLSearchParams) => Extract<OfficeShellPanelState, { panelType: PT }> }`

/**
 * Get deep link parameters for a specific side panel type
 *
 * @param overridePanelType
 */
export function getSidePanelDeepLink<PT extends OfficeShellPanelType>(
  overridePanelType: PT
): Extract<OfficeShellPanelState, { panelType: PT }> | undefined;

/**
 * Get deep link parameters
 */
export function getSidePanelDeepLink(): OfficeShellPanelState | undefined;

export function getSidePanelDeepLink(overridePanelType?: OfficeShellPanelType): OfficeShellPanelState | undefined {
  const urlParams = new URLSearchParams(window.location.search);
  const panelTypeParam = overridePanelType ?? (getSidePanelParam(urlParams, 'open') as OfficeShellPanelType | null);

  const telemetryProps = {
    panelTypeParam,
    overridePanelType,
  };

  if (!OfficeShellPanelType.some((panelType) => panelType === panelTypeParam) || !panelTypeParam) {
    if (panelTypeParam) {
      // Log only when an invalid type is passed in url
      // otherwise, we'll log this on every page load where panel type param is not passed
      TelemetryProvider.Instance.trackEvent('InvalidSidePanelType', telemetryProps);
    }

    return undefined;
  }

  TelemetryProvider.Instance.trackEvent('SidePanelDeepLink', telemetryProps);

  return sidePanelDeepLinkParamParsers[panelTypeParam](urlParams);
}
